import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    Availability,
    Certificate,
    Education,
    Experience,
    Portfolio,
    ProfessionalState,
    UserSkill,
} from "../../modules/professional/models/ProfessionalInterface";
import {
    deleteUserAvailabilities,
    getUserAvailabilities,
    getUserCertificates,
    getUserEducation,
    getUserExperience,
    getUserPortfolio,
    createAvaibility,
    UpdateUserAvailabilities,
    getUserSkills,
    createSkills,
    UpdateUserSkills,
    deleteUserSkills,
    deleteUserPortfolio,
    deleteUserExperience,
    deleteUserEducation,
    deleteUserCertificate,
    getProfessionalStats,
} from "../Actions/ProfessionalActions";

const initialState: ProfessionalState = {
    availabilities: [],
    emergencyAvailabilities: [],
    error: null,
    certificates: [],
    education: [],
    experience: [],
    portfolio: [],
    skills: [],
    professionalStats: null,
    professionalStatsLoading: false,
};

const professionalSlice = createSlice({
    name: "professional",
    initialState,
    reducers: {
        clearAvailabilityError: state => {
            state.error = null;
        },
    },
    extraReducers: builder => {
        builder

            .addCase(getUserAvailabilities.fulfilled, (state, action: PayloadAction<Availability[]>) => {
                state.availabilities = action.payload.filter(item => !item.isEmergency);
                state.emergencyAvailabilities = action.payload.filter(item => item.isEmergency);
            })
            .addCase(getUserSkills.fulfilled, (state, action: PayloadAction<UserSkill[]>) => {
                state.skills = action.payload;
            })

            .addCase(createAvaibility.fulfilled, (state, action: PayloadAction<Availability[]>) => {
                // Append new availabilities to the existing state
                const newRegular = action.payload.filter(item => !item.isEmergency);
                const newEmergency = action.payload.filter(item => item.isEmergency);

                state.availabilities = [...state.availabilities, ...newRegular];
                state.emergencyAvailabilities = [...state.emergencyAvailabilities, ...newEmergency];
            })
            .addCase(createSkills.fulfilled, (state, action: PayloadAction<UserSkill[]>) => {
                // Append new availabilities to the existing state
                state.skills = [...state.skills, ...action.payload];
            })

            .addCase(UpdateUserAvailabilities.fulfilled, (state, action: PayloadAction<Availability>) => {
                const updated = action.payload;

                if (updated.isEmergency) {
                    // Update in emergencyAvailabilities
                    const index = state.emergencyAvailabilities.findIndex(item => item.id === updated.id);
                    if (index !== -1) {
                        state.emergencyAvailabilities[index] = updated;
                    } else {
                        // If it wasn't emergency before but now is, move from regular to emergency
                        const regularIndex = state.availabilities.findIndex(item => item.id === updated.id);
                        if (regularIndex !== -1) {
                            state.availabilities.splice(regularIndex, 1);
                            state.emergencyAvailabilities.push(updated);
                        }
                    }
                } else {
                    // Update in regular availabilities
                    const index = state.availabilities.findIndex(item => item.id === updated.id);
                    if (index !== -1) {
                        state.availabilities[index] = updated;
                    } else {
                        // If it was emergency before but now isn't, move from emergency to regular
                        const emergencyIndex = state.emergencyAvailabilities.findIndex(item => item.id === updated.id);
                        if (emergencyIndex !== -1) {
                            state.emergencyAvailabilities.splice(emergencyIndex, 1);
                            state.availabilities.push(updated);
                        }
                    }
                }
            })

            .addCase(UpdateUserSkills.fulfilled, (state, action: PayloadAction<UserSkill>) => {
                // Find and update the specific skills
                const updated = action.payload;
                const index = state.skills.findIndex(item => item.id === updated.id);
                if (index !== -1) {
                    state.skills[index] = updated;
                }
            })
            .addCase(deleteUserPortfolio.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
                state.portfolio = state.portfolio.filter(item => item.id !== action.payload.id);
            })
            .addCase(deleteUserExperience.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
                state.experience = state.experience.filter(item => item.id !== action.payload.id);
            })
            .addCase(deleteUserEducation.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
                state.education = state.education.filter(item => item.id !== action.payload.id);
            })
            .addCase(deleteUserCertificate.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
                state.certificates = state.certificates.filter(item => item.id !== action.payload.id);
            })
            .addCase(deleteUserAvailabilities.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
                // Try to delete from both arrays (only one will have it)
                state.availabilities = state.availabilities.filter(item => item.id !== action.payload.id);
                state.emergencyAvailabilities = state.emergencyAvailabilities.filter(
                    item => item.id !== action.payload.id
                );
            })
            .addCase(deleteUserSkills.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
                state.skills = state.skills.filter(item => item.id !== action.payload.id);
            })
            // Certificates
            .addCase(getUserCertificates.fulfilled, (state, action: PayloadAction<Certificate[]>) => {
                state.certificates = action.payload;
            })
            .addCase(getUserEducation.fulfilled, (state, action: PayloadAction<Education[]>) => {
                state.education = action.payload;
            })
            .addCase(getUserExperience.fulfilled, (state, action: PayloadAction<Experience[]>) => {
                state.experience = action.payload;
            })

            .addCase(getUserPortfolio.fulfilled, (state, action: PayloadAction<Portfolio[]>) => {
                state.portfolio = action.payload;
            })
            .addCase(getProfessionalStats.pending, (state) => {
                state.professionalStatsLoading = true;
                state.error = null;
            })
            .addCase(getProfessionalStats.fulfilled, (state, action) => {
                state.professionalStats = action.payload;
                state.professionalStatsLoading = false;
                state.error = null;
            })
            .addCase(getProfessionalStats.rejected, (state, action) => {
                state.professionalStatsLoading = false;
                state.error = typeof action.payload === "string" ? action.payload : "Failed to fetch professional stats";
            });
    },
});

export const { clearAvailabilityError } = professionalSlice.actions;
export default professionalSlice.reducer;
